import { Injectable, NgZone, DOCUMENT, inject } from '@angular/core';

import { EnvironmentKeys } from 'src/environments/environment-keys';
import { InAppBrowserService } from './in-app-browser.service';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { Browser } from '@capacitor/browser';
import { LoggerService } from './logger.service';
import { ConfigService } from './json-config.service';
import { NotificationService } from './notification.service';
import { DeviceService } from './device.service';
import { TranslateService } from '@ngx-translate/core';
import { DashboardRoute } from 'src/models/routes.model';
import { EApiDevicePlatforms } from 'src/models/app.model';

@Injectable({
  providedIn: 'root'
})
export class RedirectService {
  private browserDocument = inject<Document>(DOCUMENT);
  private configService = inject(ConfigService);
  private loggerService = inject(LoggerService);
  private browserService = inject(InAppBrowserService);
  private zone = inject(NgZone);
  private router = inject(Router);
  private notificationService = inject(NotificationService);
  private deviceService = inject(DeviceService);
  private translateService = inject(TranslateService);

  private browserClose$: null | Subscription = null;
  private urlChange$: null | Subscription = null;

  private _localAppUrl = '';

  public get localAppUrl(): string {
    const origin = window?.location?.origin;
    if(origin) {
      this._localAppUrl = origin;
    }

    return this._localAppUrl;
  }

  public goToAmlFormExternalForm(): void {
    this.goToExternalUrl(
      EnvironmentKeys.amlFormExternalUrl,
      'Failed to get AML form redirect url, check configuration!'
    );
  }

  private goToExternalUrl(key: EnvironmentKeys, errorMsg: string) {
    const externalUrl = this.configService.getConfig(key);
    if (!externalUrl) {
      this.loggerService.error(errorMsg);
      return;
    }

    const redirectUrl = externalUrl + '?redirect_after_complete_uri=' + this.localAppUrl;

    if(!this.deviceService.isNativePlatform) {
      this.browserDocument.location.href = redirectUrl;
    } else {
      this.handleNativeBrowser(redirectUrl);
    }
  }

  private goToExternalUrlDirectly(key: EnvironmentKeys, errorMsg: string) {
    const externalUrl = this.configService.getConfig(key);
    if (!externalUrl) {
      this.loggerService.error(errorMsg);
      return;
    }

    Browser.open({ url: externalUrl });
  }

  private handleNativeBrowser(externalUrl: string): void {
    this.browserService.open(externalUrl);

    if(!this.browserService.networkConnected) {
      this.browserService.close();
      this.notificationService.error(
        this.translateService.instant('toastNotifications.headers.error'),
        this.translateService.instant('toastNotifications.messages.browserQuestionaireLoadError'),
      );
      return;
    }

    this.urlChange$ = this.browserService.onUrlChange().subscribe((navUrl) => {
      if (navUrl.startsWith(this.localAppUrl)) {
        this.zone.run(() => {
          this.router.navigate(['/', DashboardRoute.DASHBOARD], { replaceUrl: true });
        });
        this.browserService.close();
        this.unsubBrowser();
      }
    });

    this.browserClose$ = this.browserService.onBrowserClose().subscribe(() => {
      this.unsubBrowser();
    });
  }

  public goToAppStore(device: EApiDevicePlatforms): void {
    if (device === EApiDevicePlatforms.ANDROID) {
      this.goToExternalUrlDirectly(
        EnvironmentKeys.appInAndroidGoogleStoreExternalUrl,
        'Failed to redirect to Google Store, please check external url in config!'
      );
    }else if (device === EApiDevicePlatforms.IOS)
      this.goToExternalUrlDirectly(
        EnvironmentKeys.appInAppleAppStoreExternalUrl,
        'Failed to redirect to App Store, please check external url in config!'
      );
    else {
      this.loggerService.error('Unsupported device type: ' + device);
    }
  }

  private unsubBrowser(): void {
    this.browserClose$?.unsubscribe();
    this.urlChange$?.unsubscribe();
    this.browserClose$ = null;
    this.urlChange$ = null;
  }
}
